import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useResource } from "../hooks";
import { api, fmtINR, fmtNum } from "../api";
import AskOIM from "../components/AskOIM";
import { ArrowLeft } from "lucide-react";

/**
 * Twin Detail — deep view per spec section "Twin Layout":
 *   Left: animated process flow
 *   Center: current performance vs benchmark
 *   Right: AI Recommendation panel
 *
 * Hydrogenation is the hero (full custom). Other twins get a generic but still-rich layout.
 */

const HYDRO_FLOW = ({ s }) => (
  <div className="flow-diagram">
    <div className="flow-node">
      <div className="node-label">Nitrobenzene Feed</div>
      <div className="node-value">{fmtNum((s.throughput_tph || 0) * 1.32, 2)}<span style={{ fontSize: 10, marginLeft: 4, color: "var(--text-tertiary)" }}>t/h</span></div>
    </div>
    <div className="flow-arrow">→</div>
    <div className="flow-node reactor">
      <div className="node-label">Hydrogenation Reactor</div>
      <div className="node-value">{fmtNum(s.conversion_pct, 1)}<span style={{ fontSize: 10, marginLeft: 4, color: "var(--text-tertiary)" }}>% conv.</span></div>
      <div style={{ marginTop: 10, fontSize: 10, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", letterSpacing: "0.08em" }}>
        Catalyst {fmtNum(s.catalyst_activity_pct, 1)}% · H₂ purity {fmtNum(s.h2_purity_pct, 2)}%
      </div>
    </div>
    <div className="flow-arrow">→</div>
    <div className="flow-node">
      <div className="node-label">Aniline Product</div>
      <div className="node-value">{fmtNum(s.throughput_tph, 2)}<span style={{ fontSize: 10, marginLeft: 4, color: "var(--text-tertiary)" }}>t/h</span></div>
      <div style={{ marginTop: 10, fontSize: 10, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
        Yield {fmtNum(s.yield_pct, 1)}%
      </div>
    </div>
  </div>
);

const BOILER_FLOW = ({ s }) => {
  const eff = s.efficiency_pct || 0;
  const effColor = eff < 78 ? "var(--status-critical)" : eff < 84 ? "var(--brand-amber-soft)" : "var(--brand-green-soft)";
  return (
    <div className="flow-diagram" style={{ gridTemplateColumns: "1fr auto 1.4fr auto 1fr" }}>
      <div className="flow-node">
        <div className="node-label">Fuel + Air</div>
        <div className="node-value">{fmtNum(s.fuel_kgh, 0)}<span style={{ fontSize: 10, marginLeft: 4, color: "var(--text-tertiary)" }}>kg/h</span></div>
        <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>Natural Gas</div>
      </div>
      <div className="flow-arrow">→</div>
      <div className="flow-node reactor" style={{ borderColor: effColor }}>
        <div className="node-label">Combustion + Boiler Drum</div>
        <div className="node-value" style={{ color: effColor }}>η {fmtNum(eff, 1)}<span style={{ fontSize: 11, marginLeft: 3, color: "var(--text-tertiary)" }}>%</span></div>
        <div style={{ marginTop: 10, fontSize: 10, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em" }}>
          Scope-1 <span style={{ color: "var(--brand-amber-soft)", fontWeight: 600 }}>{fmtNum(s.scope1_kgh, 0)} kgCO₂/h</span>
        </div>
        <div style={{ marginTop: 4, fontSize: 10, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
          Bench η {fmtNum(s.benchmark_eff, 1)}%
        </div>
      </div>
      <div className="flow-arrow">→</div>
      <div className="flow-node">
        <div className="node-label">Steam to Plant</div>
        <div className="node-value">{fmtNum(s.steam_output_tph, 1)}<span style={{ fontSize: 10, marginLeft: 4, color: "var(--text-tertiary)" }}>t/h</span></div>
        <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>To process header</div>
      </div>
    </div>
  );
};

const DIST_FLOW = ({ s }) => {
  const purity = s.purity_pct || 0;
  const reflux = s.reflux_ratio || 0;
  const purityColor = purity < 99.0 ? "var(--brand-amber-soft)" : "var(--brand-green-soft)";
  return (
    <div className="flow-diagram" style={{ gridTemplateColumns: "1fr auto 1.4fr auto 1fr", alignItems: "stretch" }}>
      <div className="flow-node">
        <div className="node-label">Crude Aniline Feed</div>
        <div className="node-value">{fmtNum(s.feed_tph, 2)}<span style={{ fontSize: 10, marginLeft: 4, color: "var(--text-tertiary)" }}>t/h</span></div>
        <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>From hydrogenation</div>
      </div>
      <div className="flow-arrow">→</div>
      <div className="flow-node reactor" style={{ padding: 14, position: "relative" }}>
        <div className="node-label">Distillation Column</div>
        {/* Tray visualization */}
        <div style={{ display: "flex", justifyContent: "center", margin: "10px 0" }}>
          <div style={{ width: 40, height: 80, border: "1px solid var(--border-strong)", display: "flex", flexDirection: "column", justifyContent: "space-evenly", padding: 2, background: "var(--bg-base)" }}>
            {[0, 1, 2, 3, 4].map(i => <div key={i} style={{ height: 2, background: i < Math.min(5, Math.round(reflux * 1.5)) ? "var(--brand-amber)" : "var(--border-strong)" }} />)}
          </div>
        </div>
        <div className="node-value" style={{ fontSize: 14, color: purityColor }}>
          Reflux R={fmtNum(reflux, 2)}
        </div>
        <div style={{ marginTop: 6, fontSize: 10, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
          Steam <span style={{ color: "var(--brand-amber-soft)", fontWeight: 600 }}>{fmtNum(s.steam_tph, 2)} t/h</span>
        </div>
      </div>
      <div className="flow-arrow" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div>↗</div>
        <div>↘</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="flow-node" style={{ padding: 10 }}>
          <div className="node-label">Distillate (Pure Aniline)</div>
          <div className="node-value" style={{ fontSize: 16, color: purityColor }}>{fmtNum(purity, 2)}<span style={{ fontSize: 10, marginLeft: 3, color: "var(--text-tertiary)" }}>%</span></div>
        </div>
        <div className="flow-node" style={{ padding: 10 }}>
          <div className="node-label">Bottoms (Heavies)</div>
          <div className="node-value" style={{ fontSize: 14, color: "var(--text-secondary)" }}>{fmtNum(s.feed_tph * 0.02, 2)}<span style={{ fontSize: 10, marginLeft: 3, color: "var(--text-tertiary)" }}>t/h</span></div>
        </div>
      </div>
    </div>
  );
};

const GENERIC_FLOW = ({ name, s }) => (
  <div className="flow-diagram" style={{ minHeight: 200 }}>
    <div className="flow-node">
      <div className="node-label">Inputs</div>
      <div className="node-value" style={{ fontSize: 13 }}>—</div>
    </div>
    <div className="flow-arrow">→</div>
    <div className="flow-node reactor">
      <div className="node-label">{name}</div>
      <div className="node-value">Live</div>
    </div>
    <div className="flow-arrow">→</div>
    <div className="flow-node">
      <div className="node-label">Outputs</div>
      <div className="node-value" style={{ fontSize: 13 }}>—</div>
    </div>
  </div>
);

const METRIC_DEFS = {
  hydrogenation: [
    { k: "yield_pct", label: "Aniline Yield", unit: "%", bench: "benchmark_yield", digits: 2, invert: false },
    { k: "hydrogen_intensity_kg_per_t", label: "Hydrogen Intensity", unit: "kg/t", bench: "benchmark_h2", digits: 1, invert: true },
    { k: "steam_intensity_t_per_t", label: "Steam Intensity", unit: "t/t", bench: "benchmark_steam", digits: 2, invert: true },
    { k: "catalyst_activity_pct", label: "Catalyst Activity", unit: "%", bench: null, digits: 1, invert: false },
    { k: "conversion_pct", label: "Conversion", unit: "%", bench: null, digits: 2, invert: false },
    { k: "selectivity_pct", label: "Selectivity", unit: "%", bench: null, digits: 2, invert: false },
  ],
  boiler: [
    { k: "efficiency_pct", label: "Efficiency", unit: "%", bench: "benchmark_eff", digits: 1, invert: false },
    { k: "fuel_kgh", label: "Fuel Flow", unit: "kg/h", bench: null, digits: 0, invert: true },
    { k: "steam_output_tph", label: "Steam Output", unit: "t/h", bench: null, digits: 1, invert: false },
    { k: "scope1_kgh", label: "Scope-1", unit: "kg CO₂/h", bench: null, digits: 0, invert: true },
  ],
  cooling_tower: [
    { k: "approach_c", label: "Approach", unit: "°C", bench: "benchmark_approach", digits: 2, invert: true },
    { k: "heat_rejection_mw", label: "Heat Rejection", unit: "MW", bench: null, digits: 2, invert: false },
    { k: "fan_kw", label: "Fan Power", unit: "kW", bench: null, digits: 0, invert: true },
    { k: "pump_kw", label: "Pump Power", unit: "kW", bench: null, digits: 0, invert: true },
  ],
  chiller: [
    { k: "cop", label: "COP", unit: "", bench: "benchmark_cop", digits: 2, invert: false },
    { k: "cooling_load_kw", label: "Load", unit: "kW", bench: null, digits: 0, invert: false },
    { k: "power_kw", label: "Power", unit: "kW", bench: null, digits: 0, invert: true },
    { k: "scope2_kgh", label: "Scope-2", unit: "kg CO₂/h", bench: null, digits: 0, invert: true },
  ],
  compressor: [
    { k: "leak_pct", label: "Leakage", unit: "%", bench: "benchmark_leak", digits: 1, invert: true },
    { k: "delivered_nm3h", label: "Delivered", unit: "Nm³/h", bench: null, digits: 0, invert: false },
    { k: "flow_nm3h", label: "Generated", unit: "Nm³/h", bench: null, digits: 0, invert: false },
    { k: "power_kw", label: "Power", unit: "kW", bench: null, digits: 0, invert: true },
  ],
  nitration: [
    { k: "yield_pct", label: "NB Yield", unit: "%", bench: "benchmark_yield", digits: 2, invert: false },
    { k: "conversion_pct", label: "Conversion", unit: "%", bench: null, digits: 1, invert: false },
    { k: "selectivity_pct", label: "Selectivity", unit: "%", bench: null, digits: 2, invert: false },
    { k: "temperature_c", label: "Reactor Temp", unit: "°C", bench: null, digits: 1, invert: true },
  ],
  distillation: [
    { k: "purity_pct", label: "Product Purity", unit: "%", bench: "benchmark_purity", digits: 2, invert: false },
    { k: "reflux_ratio", label: "Reflux Ratio", unit: "", bench: null, digits: 2, invert: true },
    { k: "steam_intensity_t_per_t", label: "Steam Intensity", unit: "t/t", bench: null, digits: 2, invert: true },
    { k: "steam_tph", label: "Steam Demand", unit: "t/h", bench: null, digits: 2, invert: true },
  ],
  solvent: [
    { k: "recovery_pct", label: "Recovery", unit: "%", bench: "benchmark_recovery", digits: 2, invert: false },
    { k: "recovered_kgh", label: "Recovered", unit: "kg/h", bench: null, digits: 0, invert: false },
    { k: "loss_kgh", label: "Loss", unit: "kg/h", bench: null, digits: 0, invert: true },
    { k: "input_kgh", label: "Input", unit: "kg/h", bench: null, digits: 0, invert: false },
  ],
};

const REC_TEMPLATES = {
  hydrogenation: (s) => ({
    cause: s.catalyst_activity_pct < 80
      ? `Catalyst activity at ${fmtNum(s.catalyst_activity_pct, 1)}% — below 80% threshold.`
      : "Operating within nominal envelope.",
    actions: s.catalyst_activity_pct < 80 ? [
      "Schedule partial catalyst reload (vendor lead-time 4–6 wk).",
      "Raise H₂ partial pressure 0.3 bar to recoup ~30% of yield loss.",
      "Verify H₂ purity at battery limit (target ≥ 99.5%).",
    ] : [
      "Maintain current operating envelope.",
      "Calibrate reactor outlet analyser at next opportunity.",
    ],
    confidence: Math.min(95, 60 + (100 - s.catalyst_activity_pct) * 0.4),
  }),
  boiler: (s) => ({
    cause: s.efficiency_pct < 78
      ? `Boiler efficiency at ${fmtNum(s.efficiency_pct, 1)}% — significantly below ${fmtNum(s.benchmark_eff, 0)}% benchmark. Fuel firing and Scope-1 emissions are both elevated.`
      : s.efficiency_pct < 84
        ? `Boiler efficiency at ${fmtNum(s.efficiency_pct, 1)}% — modest drift below benchmark.`
        : "Boiler tracking benchmark within control band.",
    actions: s.efficiency_pct < 82 ? [
      "Plan acid clean of economizer banks at next scheduled outage.",
      "Increase soot-blow frequency to 2× per shift while degradation persists.",
      "Verify O₂ trim controller — excess air >3.5% can mimic fouling.",
      "Inspect steam traps in main header for stuck-open failures.",
    ] : [
      "Continue scheduled maintenance.",
      "Trend Δ-pressure across economizer for early fouling detection.",
    ],
    confidence: Math.min(92, 60 + (s.benchmark_eff - s.efficiency_pct) * 3),
  }),
  distillation: (s) => ({
    cause: s.purity_pct < 99.4
      ? `Product purity at ${fmtNum(s.purity_pct, 2)}% — drifted below ${fmtNum(s.benchmark_purity, 2)}% spec. Reflux ratio elevated to ${fmtNum(s.reflux_ratio, 2)} to compensate.`
      : `Column holding ${fmtNum(s.purity_pct, 2)}% purity with reflux ratio ${fmtNum(s.reflux_ratio, 2)}.`,
    actions: s.reflux_ratio > 1.9 ? [
      "Schedule tray washing at next planned shutdown window.",
      "Trial incremental reflux reduction — find minimum that still holds purity spec.",
      "Inspect condenser duty — restricted cooling forces higher reflux.",
      "Sample column at intermediate trays to confirm fouling vs flooding.",
    ] : [
      "Maintain current operating profile.",
      "Monitor steam intensity for early fouling signature.",
    ],
    confidence: Math.min(90, 60 + (s.reflux_ratio - 1.6) * 70),
  }),
  compressor: (s) => ({
    cause: s.leak_pct > 8 ? `Leak rate ${fmtNum(s.leak_pct, 1)}% (target 5%).` : "Within target.",
    actions: s.leak_pct > 8 ? [
      "Ultrasonic leak survey on plant air headers.",
      "Tag-and-fix program — weekly closure tracking.",
    ] : ["Continue scheduled audits."],
    confidence: 88,
  }),
};

const DEFAULT_REC = (twin_key) => ({
  cause: "Operating within nominal envelope.",
  actions: ["Maintain current setpoints.", "Review sensor calibration at next opportunity."],
  confidence: 75,
});

export default function TwinDetail() {
  const { key } = useParams();
  const nav = useNavigate();
  const twin = useResource(() => api.twin(key), [key], 2000);
  if (!twin) return <div className="muted">Loading twin…</div>;
  const s = twin.state;

  const defs = METRIC_DEFS[key] || [];
  const rec = (REC_TEMPLATES[key] || DEFAULT_REC)(s);

  return (
    <div className="stack" data-testid={`twin-detail-${key}`}>
      <div className="page-title">
        <div className="flex">
          <button className="btn" onClick={() => nav("/operations")} data-testid="back-to-ops">
            <ArrowLeft size={14} /> Back
          </button>
          <div>
            <h1>{twin.name} — Digital Twin</h1>
            <div className="sub">{twin.category.toUpperCase()} · live every 2s</div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 16 }}>
        {/* LEFT: animated flow */}
        <div className="card" data-testid="twin-flow">
          <div className="card-header"><div className="card-title">Process Flow</div><span className="chip">Live</span></div>
          <div className="card-body" style={{ padding: 16 }}>
            {key === "hydrogenation"
              ? <HYDRO_FLOW s={s} />
              : key === "boiler"
                ? <BOILER_FLOW s={s} />
                : key === "distillation"
                  ? <DIST_FLOW s={s} />
                  : <GENERIC_FLOW name={twin.name} s={s} />}
            <div style={{ marginTop: 16, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-tertiary)", letterSpacing: "0.05em" }}>
              Leakage rate · <span style={{ color: "var(--brand-amber-soft)", fontWeight: 600 }}>{fmtINR(s.leakage_inrh || 0)}/hr</span>
            </div>
          </div>
        </div>

        {/* CENTER: current performance vs benchmark */}
        <div className="card" data-testid="twin-metrics">
          <div className="card-header"><div className="card-title">Performance vs Benchmark</div></div>
          <div className="card-body" style={{ padding: 0 }}>
            <table className="tbl">
              <thead>
                <tr><th>Metric</th><th style={{ textAlign: "right" }}>Current</th><th style={{ textAlign: "right" }}>Bench.</th></tr>
              </thead>
              <tbody>
                {defs.map(d => {
                  const cur = s[d.k];
                  const bn = d.bench ? s[d.bench] : null;
                  return (
                    <tr key={d.k} data-testid={`twin-metric-${d.k}`}>
                      <td className="muted">{d.label}</td>
                      <td className="mono" style={{ textAlign: "right", color: "var(--text-primary)" }}>{fmtNum(cur, d.digits)} <span className="muted" style={{ fontSize: 10 }}>{d.unit}</span></td>
                      <td className="mono" style={{ textAlign: "right", color: "var(--text-tertiary)" }}>{bn != null ? fmtNum(bn, d.digits) : "—"}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td className="muted">Economic Impact</td>
                  <td colSpan={2} className="amber-color" style={{ textAlign: "right", fontWeight: 600 }}>{fmtINR(s.leakage_inrh || 0)}/hr</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* RIGHT: AI recommendation panel */}
        <div className="card" data-testid="twin-ai-panel">
          <div className="card-header">
            <div className="card-title">AI Recommendation</div>
            <span className="chip" style={{ color: "var(--brand-amber)" }}>{Math.round(rec.confidence)}% conf.</span>
          </div>
          <div className="card-body">
            <div style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 6 }}>Likely Cause</div>
            <div style={{ fontSize: 13.5, color: "var(--text-primary)", lineHeight: 1.55, marginBottom: 16 }}>{rec.cause}</div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 6 }}>Suggested Actions</div>
            <ol style={{ paddingLeft: 18, margin: 0, color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.65 }}>
              {rec.actions.map((a, i) => <li key={i} style={{ marginBottom: 5 }}>{a}</li>)}
            </ol>
            <AskOIM question={`Give me a deep analysis of ${twin.name} performance right now.`} source={key} />
          </div>
        </div>
      </div>
    </div>
  );
}
